(() => {
  'use strict';
  const RAD = Math.PI / 180;
  const EARTH = 6371008.8;
  const geo = {
    distance(a, b) {
      const h = Math.sin((b[0] - a[0]) * RAD / 2) ** 2 + Math.cos(a[0] * RAD) * Math.cos(b[0] * RAD) * Math.sin((b[1] - a[1]) * RAD / 2) ** 2;
      return 2 * EARTH * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
    },
    ring(center, radius) {
      const lat = center[0] * RAD, lon = center[1] * RAD, d = radius / EARTH;
      return Array.from({length: 181}, (_, i) => {
        const bearing = i * 2 * RAD;
        const y = Math.asin(Math.sin(lat) * Math.cos(d) + Math.cos(lat) * Math.sin(d) * Math.cos(bearing));
        const x = lon + Math.atan2(Math.sin(bearing) * Math.sin(d) * Math.cos(lat), Math.cos(d) - Math.sin(lat) * Math.sin(y));
        return [y / RAD, x / RAD];
      });
    },
    zone(metres, free, paid) {
      return metres <= free * 1000 ? 'free' : metres <= paid * 1000 ? 'paid' : 'outside';
    },
    fromUTM32(easting, northing) {
      if (!Number.isFinite(easting) || !Number.isFinite(northing)) throw new Error('Invalid coordinates');
      const a = 6378137, f = 1 / 298.257222101, e2 = f * (2 - f), ep2 = e2 / (1 - e2), k = .9996;
      const mu = (northing / k) / (a * (1 - e2 / 4 - 3 * e2 ** 2 / 64 - 5 * e2 ** 3 / 256));
      const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
      const p = mu + (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu) + (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu) + 151 * e1 ** 3 / 96 * Math.sin(6 * mu) + 1097 * e1 ** 4 / 512 * Math.sin(8 * mu);
      const n = a / Math.sqrt(1 - e2 * Math.sin(p) ** 2), r = a * (1 - e2) / (1 - e2 * Math.sin(p) ** 2) ** 1.5;
      const t = Math.tan(p) ** 2, c = ep2 * Math.cos(p) ** 2, d = (easting - 500000) / (n * k);
      const lat = p - n * Math.tan(p) / r * (d ** 2 / 2 - (5 + 3 * t + 10 * c - 4 * c ** 2 - 9 * ep2) * d ** 4 / 24 + (61 + 90 * t + 298 * c + 45 * t ** 2 - 252 * ep2 - 3 * c ** 2) * d ** 6 / 720);
      const lon = (d - (1 + 2 * t + c) * d ** 3 / 6 + (5 - 2 * c + 28 * t - 3 * c ** 2 + 8 * ep2 + 24 * t ** 2) * d ** 5 / 120) / Math.cos(p);
      const point = [lat / RAD, 9 + lon / RAD];
      if (point[0] < 54 || point[0] > 58.2 || point[1] < 7.5 || point[1] > 16) throw new Error('Coordinates outside Denmark');
      return point;
    }
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = geo;
  if (typeof window === 'undefined' || customElements.get('bs-delivery-check')) return;
  const cache = new Map();
  let leafletPromise;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function loadLeaflet(config) {
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = config.leafletCss;
      const script = document.createElement('script');
      script.src = config.leafletJs; script.async = true;
      let cssReady = false, jsReady = false, library;
      const timer = setTimeout(fail, 15000);
      function fail() { clearTimeout(timer); link.remove(); script.remove(); leafletPromise = null; reject(new Error('Map assets unavailable')); }
      function done() { if (cssReady && jsReady) { clearTimeout(timer); resolve(library); } }
      link.onload = () => { cssReady = true; done(); };
      script.onload = () => { if (!window.L?.map) { fail(); return; } library = window.L.noConflict(); jsReady = true; done(); };
      link.onerror = fail; script.onerror = fail;
      document.head.append(link, script);
    });
    return leafletPromise;
  }
  async function getAddressJSON(path, params, signal) {
    const url = new URL(path, 'https://adressevaelger.dk/');
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
    const key = url.href;
    if (cache.has(key)) return cache.get(key);
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    signal.addEventListener('abort', abort, {once: true});
    const timer = setTimeout(() => controller.abort('timeout'), 10000);
    try {
      const response = await fetch(url, {signal: controller.signal, credentials: 'omit', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Address service unavailable');
      const json = await response.json();
      if (json.status !== 'ok') throw new Error('Invalid address response');
      cache.set(key, json);
      if (cache.size > 60) cache.delete(cache.keys().next().value);
      return json;
    } finally { clearTimeout(timer); signal.removeEventListener('abort', abort); }
  }
  class DeliveryCheck extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.config = JSON.parse(this.querySelector('[data-bs-config]').textContent);
      this.t = this.config.text;
      this.input = this.querySelector('[data-bs-input]');
      this.options = this.querySelector('[data-bs-options]');
      this.hint = this.querySelector('[data-bs-hint]');
      this.result = this.querySelector('[data-bs-result]');
      this.mapNode = this.querySelector('[data-bs-map]');
      this.mapState = this.querySelector('[data-bs-map-state]');
      this.resetButton = this.querySelector('[data-bs-reset]');
      this.ui = new AbortController(); this.generation = (this.generation || 0) + 1;
      this.sequence = 0; this.activeIndex = -1; this.results = []; this.selected = null;
      this.style.setProperty('--bs-free', this.config.green);
      this.style.setProperty('--bs-paid', this.config.terra);
      const on = (element, event, fn) => element.addEventListener(event, fn, {signal: this.ui.signal});
      on(this.input, 'input', () => {
        this.cancelSearch(); this.results = []; this.options.replaceChildren(); this.clearSelection(); this.closeOptions();
        this.resetButton.hidden = !this.input.value;
        if (this.input.value.trim().length < 3) { this.message(this.t.hint); return; }
        this.message(this.t.searching, 'loading');
        this.debounce = setTimeout(() => this.search(), 260);
      });
      on(this.querySelector('[data-bs-form]'), 'submit', event => {
        event.preventDefault(); clearTimeout(this.debounce);
        if (!this.options.hidden && this.activeIndex >= 0) this.choose(this.results[this.activeIndex]); else this.search();
      });
      on(this.input, 'keydown', event => {
        if (event.key === 'Escape' && !this.options.hidden) { event.preventDefault(); event.stopPropagation(); this.closeOptions(); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || this.options.hidden) return;
        event.preventDefault();
        this.activeIndex = event.key === 'Home' ? 0 : event.key === 'End' ? this.results.length - 1 : (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + this.results.length) % this.results.length;
        [...this.options.children].forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
        const option = this.options.children[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({block:'nearest'});
      });
      on(this.options, 'pointerdown', event => event.preventDefault());
      on(this.options, 'click', event => { const option = event.target.closest('[data-index]'); if (option) this.choose(this.results[Number(option.dataset.index)]); });
      on(this.input, 'focus', () => { if (this.results.length && !this.selected) this.openOptions(); });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) this.closeOptions(); });
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.closeOptions(); });
      on(this.resetButton, 'click', () => this.reset());
      on(this.querySelector('[data-bs-zoom-in]'), 'click', () => this.map?.zoomIn());
      on(this.querySelector('[data-bs-zoom-out]'), 'click', () => this.map?.zoomOut());
      on(this.querySelector('[data-bs-fit]'), 'click', () => this.fit());
      on(this.querySelector('[data-bs-retry]'), 'click', () => { if (this.map) { this.tileGood = 0; this.tileBad = 0; this.showMapState(this.t.loadingMap); this.tiles.redraw(); this.tileWatch(); } else this.ensureMap(); });
      this.resize = new ResizeObserver(() => {
        if (!this.map || !this.mapNode.clientWidth || !this.mapNode.clientHeight) return;
        this.map.invalidateSize({pan:false});
        if (!this.selected && !this.userMoved) this.fit(false);
      });
      this.resize.observe(this.mapNode);
      if (!this.hasAttribute('data-modal')) {
        this.intersection = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { this.intersection.disconnect(); this.ensureMap(); } }, {rootMargin: '250px'});
        this.intersection.observe(this);
      }
    }
    disconnectedCallback() {
      this.connected = false; this.generation++; this.ui?.abort(); this.cancelSearch();
      this.intersection?.disconnect(); this.resize?.disconnect(); clearTimeout(this.tileTimer);
      this.map?.remove(); this.map = null; this.mapStarting = false;
    }
    message(text, state = 'idle') { this.hint.textContent = text; this.dataset.searchState = state; this.input.setAttribute('aria-busy', String(state === 'loading')); }
    cancelSearch() { clearTimeout(this.debounce); this.request?.abort(); this.sequence++; }
    closeOptions() { this.options.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant'); this.activeIndex = -1; }
    openOptions() { this.options.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    clearSelection() { this.selected = null; this.result.hidden = true; if (this.marker && this.map) this.map.removeLayer(this.marker); this.marker = null; }
    async search() {
      this.cancelSearch(); this.results = []; this.closeOptions();
      const query = this.input.value.trim();
      if (query.length < 3) { this.message(this.t.hint); return; }
      this.request = new AbortController(); const signal = this.request.signal, id = this.sequence;
      this.message(this.t.searching, 'loading');
      try {
        const data = await getAddressJSON('husnumre/soeg', {tekst: query, maksimum: 8, token: this.config.token}, signal);
        if (id !== this.sequence || !this.isConnected) return;
        this.results = (data.fund || []).filter(item => typeof item.titel === 'string').slice(0, 8);
        this.options.replaceChildren();
        if (!this.results.length) { this.message(this.t.noResults); return; }
        this.results.forEach((item, index) => {
          const option = document.createElement('li');
          option.className = 'bs-delivery__option'; option.id = `${this.input.id}-option-${index}`;
          option.dataset.index = index; option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false');
          const icon = document.createElement('span'); icon.textContent = '⌖'; icon.setAttribute('aria-hidden', 'true');
          const body = document.createElement('span'), title = document.createElement('strong'), detail = document.createElement('small');
          const parts = item.titel.match(/^(.*?)[, ]+(\d{4}\s+.*)$/);
          title.textContent = parts ? parts[1].replace(/,\s*$/, '') : item.titel;
          detail.textContent = parts ? parts[2] : (item.type === 'husnummer' ? '' : this.t.addNumber);
          body.append(title, detail); option.append(icon, body); this.options.append(option);
        });
        this.openOptions(); this.message(this.t.choose);
      } catch (error) { if (!signal.aborted && id === this.sequence) { this.closeOptions(); this.message(this.t.searchError, 'error'); } }
    }
    async choose(item) {
      if (!item) return;
      this.cancelSearch(); this.closeOptions(); this.results = []; this.clearSelection(); this.resetButton.hidden = false;
      if (item.type !== 'husnummer' || !/^[a-f0-9-]{36}$/i.test(item.id || '')) {
        const road = item.vejnavn || item.vejNavn || item.titel;
        this.input.value = item.postnr ? `${road} , ${item.postnr} ${item.postdistrikt || ''}` : `${item.titel} `;
        this.input.focus(); this.input.setSelectionRange(road.length + 1, road.length + 1);
        this.message(this.t.addNumber); return;
      }
      this.input.value = item.titel; this.message(this.t.resolving, 'loading');
      this.request = new AbortController(); const signal = this.request.signal, id = this.sequence;
      try {
        const data = await getAddressJSON(`husnumre/${item.id}`, {token: this.config.token}, signal);
        if (id !== this.sequence || !this.isConnected) return;
        const house = data.husnummer, geometry = house?.adgangspunkt?.geometri;
        if (geometry?.crs?.properties?.name !== 'EPSG:25832' || geometry.type !== 'Point') throw new Error('Unexpected address coordinates');
        const point = geo.fromUTM32(...geometry.coordinates);
        this.selected = {point, label: house.adgangsadressebetegnelse || item.titel};
        const metres = geo.distance(this.config.center, point), zone = geo.zone(metres, this.config.free, this.config.paid);
        this.result.dataset.zone = zone; this.result.hidden = false;
        this.querySelector('[data-bs-result-icon]').textContent = zone === 'outside' ? '⌖' : '✓';
        this.querySelector('[data-bs-result-title]').textContent = this.t[`${zone}Title`];
        this.querySelector('[data-bs-result-address]').textContent = this.selected.label;
        this.querySelector('[data-bs-result-distance]').textContent = `${(metres / 1000).toLocaleString('da-DK', {minimumFractionDigits: 1, maximumFractionDigits: 2})} km ${this.t.distance}`;
        this.querySelector('[data-bs-result-note]').textContent = this.t[`${zone}Note`];
        const boundary = this.querySelector('[data-bs-boundary]');
        boundary.hidden = Math.min(Math.abs(metres - this.config.free * 1000), Math.abs(metres - this.config.paid * 1000)) > 350;
        boundary.textContent = this.t.boundary;
        this.message('');
        if (this.map) this.placeSelection(); else await this.ensureMap();
      } catch (error) { if (!signal.aborted && id === this.sequence) this.message(this.t.searchError, 'error'); }
    }
    reset() {
      this.cancelSearch(); this.input.value = ''; this.results = []; this.closeOptions(); this.clearSelection();
      this.resetButton.hidden = true; this.message(this.t.hint); this.fit(); this.input.focus();
    }
    showMapState(message, retry = false) {
      this.mapState.hidden = false; this.querySelector('[data-bs-map-message]').textContent = message;
      this.querySelector('[data-bs-retry]').hidden = !retry;
    }
    tileWatch() {
      clearTimeout(this.tileTimer);
      this.tileTimer = setTimeout(() => { if (this.connected && !this.tileGood) this.showMapState(this.t.tileError, true); }, 12000);
    }
    async ensureMap() {
      if (this.map) { this.map.invalidateSize({pan:false}); return; }
      if (this.mapStarting || !this.connected) return;
      if (!this.config.center.every(Number.isFinite) || !(this.config.free > 0 && this.config.paid > this.config.free)) { this.showMapState(this.t.mapError, true); return; }
      this.mapStarting = true; const generation = this.generation;
      this.showMapState(this.t.loadingMap);
      try {
        const L = await loadLeaflet(this.config);
        if (!this.connected || generation !== this.generation) return;
        this.L = L;
        this.map = L.map(this.mapNode, {zoomControl:false, scrollWheelZoom:false, zoomSnap:.25, zoomDelta:.75, minZoom:6, maxZoom:18, zoomAnimation:!reduced(), fadeAnimation:!reduced()}).setView(this.config.center, 9);
        this.map.attributionControl.setPrefix(false);
        this.tileGood = 0; this.tileBad = 0;
        this.tiles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {maxZoom:19, attribution:'&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>', crossOrigin:true});
        this.tiles.on('tileload', () => { this.tileGood++; this.mapState.hidden = true; clearTimeout(this.tileTimer); });
        this.tiles.on('tileerror', () => { this.tileBad++; if (!this.tileGood && this.tileBad >= 4) this.showMapState(this.t.tileError, true); });
        this.tiles.on('loading', () => this.tileWatch());
        this.tiles.addTo(this.map); this.tileWatch();
        const outer = geo.ring(this.config.center, this.config.paid * 1000), inner = geo.ring(this.config.center, this.config.free * 1000);
        this.bounds = L.latLngBounds(outer);
        L.polygon([outer, [...inner].reverse()], {color:this.config.terra, fillColor:this.config.terra, fillOpacity:.13, weight:2, interactive:false, smoothFactor:0}).addTo(this.map);
        L.polygon(inner, {color:this.config.green, fillColor:this.config.green, fillOpacity:.18, weight:2, interactive:false, smoothFactor:0}).addTo(this.map);
        const icon = L.divIcon({className:'', html:'<span class="bs-delivery__pin"><span>✿</span></span>', iconSize:[36,36], iconAnchor:[18,36]});
        const popup = document.createElement('div'); const strong = document.createElement('strong'); strong.textContent = 'Blomstrende Søskende';
        popup.append(strong, document.createElement('br'), document.createTextNode(this.config.address));
        L.marker(this.config.center, {icon, title:this.config.address, alt:this.t.origin}).bindPopup(popup).addTo(this.map);
        this.map.on('dragstart', () => { this.userMoved = true; });
        this.fit(false);
        if (this.selected) this.placeSelection();
      } catch (error) {
        if (generation === this.generation && this.connected) { this.map?.remove(); this.map = null; this.showMapState(this.t.mapError, true); }
      } finally { if (generation === this.generation) this.mapStarting = false; }
    }
    fit(animate = true) { if (this.map && this.bounds) { this.userMoved = false; this.map.invalidateSize({pan:false}); this.map.fitBounds(this.bounds, {paddingTopLeft:[24,55], paddingBottomRight:[55,62], animate:animate && !reduced()}); } }
    placeSelection() {
      if (!this.map || !this.selected) return;
      if (this.marker) this.map.removeLayer(this.marker);
      const icon = this.L.divIcon({className:'',html:'<span class="bs-delivery__pin bs-delivery__pin--selected"><span>✓</span></span>',iconSize:[36,36],iconAnchor:[18,36]});
      const label = document.createElement('span'); label.textContent = this.selected.label;
      this.marker = this.L.marker(this.selected.point,{icon,title:this.selected.label,alt:this.t.selected}).bindPopup(label).addTo(this.map);
      this.map.invalidateSize({pan:false});
      this.map.fitBounds(this.L.latLngBounds([this.config.center,this.selected.point]), {padding:[65,65],maxZoom:13,animate:!reduced()});
    }
  }
  customElements.define('bs-delivery-check', DeliveryCheck);
  let returnFocus, oldOverflow;
  function openDialog(trigger) {
    const dialog = document.getElementById('bs-delivery-dialog');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (dialog.open) return true;
    returnFocus = trigger; oldOverflow = document.documentElement.style.overflow;
    dialog.showModal(); document.documentElement.style.overflow = 'hidden';
    const checker = dialog.querySelector('bs-delivery-check'); checker?.ensureMap();
    checker?.querySelector('[data-bs-input]')?.focus({preventScroll:true});
    return true;
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('a[href], [data-bs-delivery-open]');
    if (trigger && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      let opensDelivery = trigger.hasAttribute('data-bs-delivery-open');
      const href = trigger.getAttribute('href');
      if (!opensDelivery && href) {
        try {
          const target = new URL(href, document.baseURI);
          opensDelivery = target.origin === new URL(document.baseURI).origin && target.hash === '#leveringstjek';
        } catch (_) { /* An unrelated malformed link must not interrupt the shop. */ }
      }
      if (opensDelivery && openDialog(trigger)) event.preventDefault();
    }
    if (event.target.closest('[data-bs-dialog-close]')) event.target.closest('dialog')?.close();
    if (event.target.matches('dialog.bs-delivery-dialog')) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
    }
  });
  document.addEventListener('close', event => {
    if (event.target.id !== 'bs-delivery-dialog') return;
    document.documentElement.style.overflow = oldOverflow || '';
    const checker = event.target.querySelector('bs-delivery-check'); checker?.closeOptions();
    if (returnFocus?.isConnected) returnFocus.focus({preventScroll:true});
  }, true);
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.querySelector('#bs-delivery-dialog[open]')) document.documentElement.style.overflow = oldOverflow || '';
  });
})();
